// formatDateTime.js — Decisão 2 Sessão 22.
//
// Backend grava timestamps em UTC (correto pra storage timezone-agnostic).
// Frontend renderiza no timezone do tenant logado (clients.timezone, vinda
// do /auth/me como `user.client_timezone`).
//
// Antes desta utility, vários sites renderizavam UTC cru em telas onde o
// usuário esperava hora local — ex: footer do relatório HTML mostrava
// "Gerado em 13/05/2026 às 00:55" enquanto local BR era 21:55 do dia 12.
// Detalhe técnico exposto sem necessidade.
//
// Default 'America/Sao_Paulo' cobre 100% dos prospects atuais. Quando
// aparecer cliente internacional, admin do tenant ajusta clients.timezone
// e Intl.DateTimeFormat resolve sozinho.

const DEFAULT_TZ = 'America/Sao_Paulo';

/**
 * Resolve o timezone do tenant a partir de várias fontes possíveis.
 * Ordem: explícito > user.client_timezone > localStorage > default.
 *
 * @param {string|object|null} userOrTz string TZ direto, objeto user, ou null
 * @returns {string} IANA timezone string
 */
function resolveTimezone(userOrTz) {
  if (typeof userOrTz === 'string' && userOrTz) return userOrTz;
  if (userOrTz && typeof userOrTz === 'object') {
    if (userOrTz.client_timezone) return userOrTz.client_timezone;
    if (userOrTz.timezone) return userOrTz.timezone;
  }
  try {
    const cached = localStorage.getItem('user');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.client_timezone) return parsed.client_timezone;
    }
  } catch {
    // localStorage indisponível (SSR ou storage corrompido) — segue pro default
  }
  return DEFAULT_TZ;
}

/**
 * Formata data+hora pra exibição local.
 *
 * @param {string|Date|number} input ISO string, Date, ou timestamp ms
 * @param {string|object|null} tzOrUser timezone direto ou objeto user
 * @param {object} options overrides do Intl.DateTimeFormat
 * @returns {string} string formatada ou '—' se input inválido
 */
export function formatDateTime(input, tzOrUser = null, options = {}) {
  if (input === null || input === undefined || input === '') return '—';
  let date;
  try {
    date = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(date.getTime())) return '—';
  } catch {
    return '—';
  }
  const timeZone = resolveTimezone(tzOrUser);
  const fmt = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
    ...options,
  });
  return fmt.format(date);
}

/**
 * Formata só a data (sem hora).
 */
export function formatDate(input, tzOrUser = null) {
  return formatDateTime(input, tzOrUser, {
    hour: undefined,
    minute: undefined,
  });
}

/**
 * Formata só a hora (sem data).
 */
export function formatTime(input, tzOrUser = null) {
  return formatDateTime(input, tzOrUser, {
    day: undefined,
    month: undefined,
    year: undefined,
  });
}

export default formatDateTime;
